<template>
  <div>
    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="card_body_bottom">
        <template #header>
          <b-row class="flex-row justify-content-between align-items-center" style="width: 100%">
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="d-flex justify-content-start align-items-center" style="gap:5px">
              <h4 class="mb-0">Report Emails</h4>
              <b-button
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                @click="load"
                v-b-tooltip.hover="'Reload'"
              >
                <feather-icon icon="RefreshCcwIcon" />
              </b-button>
            </b-col>
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="d-flex justify-content-end align-items-center" style="gap: 8px;">
              <b-badge v-if="dirty" variant="light-warning">Unsaved changes</b-badge>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                size="sm"
                :disabled="saving"
                @click="save"
              >
                <b-spinner v-if="saving" small class="mr-50" />
                <feather-icon v-else icon="SaveIcon" class="mr-50" />
                <span class="align-middle">Save</span>
              </b-button>
            </b-col>
          </b-row>
        </template>

        <b-card-body>
          <b-alert variant="primary" show class="mb-1">
            <div class="alert-body" style="font-size: 0.78rem;">
              Each report is emailed once a day, covering the trades <strong>booked</strong> (trade date) that day.
              Every recipient receives a file limited to the accounts they are allowed to see, so a client
              only ever gets their own trades. If nothing is booked for a recipient on a given day, they get no email.
            </div>
          </b-alert>

          <b-card
            v-for="report in reports"
            :key="report.ReportType"
            class="report-block mb-1"
            no-body
          >
            <div class="p-1">
              <b-row class="align-items-end">
                <b-col cols="12" md="3">
                  <b-form-checkbox v-model="report.Enabled" switch class="custom-control-primary mb-50">
                    <strong>{{ report.Label }}</strong>
                  </b-form-checkbox>
                  <small class="text-muted">
                    {{ report.Enabled ? 'Sends daily' : 'Disabled - will not send' }}
                  </small>
                </b-col>

                <b-col cols="6" md="2">
                  <label class="compact_label">Send Time</label>
                  <b-form-input
                    v-model="report.SendTime"
                    type="time"
                    size="sm"
                    :disabled="!report.Enabled"
                  />
                </b-col>

                <b-col cols="6" md="3">
                  <label class="compact_label">Timezone</label>
                  <v-select
                    v-model="report.Timezone"
                    :options="timezones"
                    :clearable="false"
                    :disabled="!report.Enabled"
                    placeholder="Timezone"
                  />
                </b-col>

                <b-col cols="12" md="4" class="d-flex justify-content-md-end align-items-center mt-50 mt-md-0" style="gap: 8px;">
                  <span class="text-muted" style="font-size: 0.75rem;">
                    {{ report.selectedUsers.length }} recipient(s), {{ report.Cc.length }} CC
                  </span>
                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    :disabled="report.selectedUsers.length === 0"
                    @click="openTest(report)"
                  >
                    <feather-icon icon="SendIcon" class="mr-50" />
                    <span class="align-middle">Test run...</span>
                  </b-button>
                </b-col>
              </b-row>

              <b-row class="mt-1">
                <b-col cols="12" md="6">
                  <label class="compact_label">Recipients</label>
                  <v-select
                    class="email-multi"
                    v-model="report.selectedUsers"
                    label="label"
                    :options="users"
                    multiple
                    :close-on-select="false"
                    :disabled="!report.Enabled"
                    placeholder="Select user(s)"
                  />
                </b-col>

                <b-col cols="12" md="6" class="mt-50 mt-md-0">
                  <label class="compact_label">CC</label>
                  <v-select
                    class="email-multi"
                    v-model="report.Cc"
                    :options="[]"
                    multiple
                    taggable
                    push-tags
                    :close-on-select="false"
                    :create-option="cleanEmail"
                    :disabled="!report.Enabled"
                    placeholder="Type an email address and press Enter"
                    @input="report.Cc = validEmails(report.Cc)"
                  >
                    <template #no-options>Type an email address and press Enter</template>
                  </v-select>
                </b-col>
              </b-row>
            </div>
          </b-card>

          <!-- Daily summary of everything above -->
          <b-card class="report-block summary-block mb-1" no-body>
            <div class="p-1">
              <b-row class="align-items-end">
                <b-col cols="12" md="3">
                  <b-form-checkbox v-model="summary.Enabled" switch class="custom-control-primary mb-50">
                    <strong>Daily Summary</strong>
                  </b-form-checkbox>
                  <small class="text-muted">
                    {{ summary.Enabled ? 'Sends daily if any report email went out' : 'Disabled - will not send' }}
                  </small>
                </b-col>

                <b-col cols="6" md="2">
                  <label class="compact_label">Send Time</label>
                  <b-form-input
                    v-model="summary.SendTime"
                    type="time"
                    size="sm"
                    :disabled="!summary.Enabled"
                  />
                </b-col>

                <b-col cols="6" md="3">
                  <label class="compact_label">Timezone</label>
                  <v-select
                    v-model="summary.Timezone"
                    :options="timezones"
                    :clearable="false"
                    :disabled="!summary.Enabled"
                    placeholder="Timezone"
                  />
                </b-col>

                <b-col cols="12" md="4" class="d-flex justify-content-md-end align-items-center mt-50 mt-md-0" style="gap: 8px;">
                  <span class="text-muted" style="font-size: 0.75rem;">
                    {{ summary.Emails.length }} address(es)
                  </span>
                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    :disabled="summary.Emails.length === 0"
                    @click="openTest(summaryAsReport)"
                  >
                    <feather-icon icon="SendIcon" class="mr-50" />
                    <span class="align-middle">Test run...</span>
                  </b-button>
                </b-col>
              </b-row>

              <b-row class="mt-1">
                <b-col cols="12">
                  <label class="compact_label">Send Summary To</label>
                  <v-select
                    class="email-multi"
                    v-model="summary.Emails"
                    :options="[]"
                    multiple
                    taggable
                    push-tags
                    :close-on-select="false"
                    :create-option="cleanEmail"
                    :disabled="!summary.Enabled"
                    placeholder="Type an email address and press Enter"
                    @input="summary.Emails = validEmails(summary.Emails)"
                  >
                    <template #no-options>Type an email address and press Enter</template>
                  </v-select>
                  <small class="text-muted">
                    Lists every report email sent or failed that day, with its recipient and CC.
                    Set the time after the latest report above so the summary includes them all.
                  </small>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-card-body>
      </b-card>

      <template #overlay v-if="loading">
        <div class="text-center d-flex justify-content-center align-items-start mt-3">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon" />
          <p id="cancel-label">Loading...</p>
        </div>
      </template>
    </b-overlay>

    <!-- Send history -->
    <b-card class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h5 class="mb-0">Recent Sends</h5>
        <span class="text-muted" style="font-size: 0.75rem;">Last {{ logs.length }} attempt(s)</span>
      </div>
      <b-table
        class="compact_table report-email-log"
        :items="logs"
        :fields="logColumns"
        :small="true"
        :hover="true"
        :striped="true"
        :bordered="true"
        headVariant="dark"
        responsive
        empty-text="Nothing has been sent yet."
        show-empty
      >
        <template #cell(Status)="data">
          <b-badge :variant="statusVariant(data.value)">{{ data.value }}</b-badge>
        </template>
        <template #cell(Message)="data">
          <span style="font-size: 0.7rem;">{{ data.value }}</span>
        </template>
      </b-table>
    </b-card>

    <!-- Test run modal -->
    <b-modal
      v-model="testModal"
      :title="'Test run - ' + (testReport ? testReport.Label : '')"
      size="lg"
      ok-only
      ok-title="Close"
      ok-variant="secondary"
    >
      <b-alert variant="warning" show class="mb-1">
        <div class="alert-body" style="font-size: 0.78rem;">
          A test run ignores the send time and the once-a-day guard.
          Leave <strong>Preview only</strong> ticked to see who would receive what
          <strong>without sending anything</strong>. Unticking it sends real email to the
          configured recipients.
          <span v-if="dirty"><br><strong>Your unsaved changes will be saved before the test runs.</strong></span>
        </div>
      </b-alert>

      <b-row class="align-items-end">
        <b-col cols="12" md="5">
          <label class="compact_label">Trade Date</label>
          <b-form-datepicker
            v-model="testDate"
            :start-weekday="1"
            :hide-header="true"
            class="form-control"
            today-button
            reset-button
          />
        </b-col>
        <b-col cols="12" md="4" style="padding-bottom: 0.4rem;">
          <b-form-checkbox v-model="testDryRun" switch class="custom-control-primary">
            Preview only (no email sent)
          </b-form-checkbox>
        </b-col>
        <b-col cols="12" md="3" style="padding-bottom: 0.4rem;">
          <b-button
            :variant="testDryRun ? 'primary' : 'danger'"
            size="sm"
            block
            :disabled="!testDate || testRunning"
            @click="runTest"
          >
            <b-spinner v-if="testRunning" small class="mr-50" />
            <span class="align-middle">{{ testDryRun ? 'Preview' : 'Send now' }}</span>
          </b-button>
        </b-col>
      </b-row>

      <div v-if="!testDryRun" class="mt-1">
        <b-alert variant="danger" show>
          <div class="alert-body" style="font-size: 0.78rem;">
            This will send real email to
            {{ testReport ? testReport.selectedUsers.length : 0 }} recipient(s)<span
              v-if="testReport && testReport.Cc && testReport.Cc.length"
            >, copying {{ testReport.Cc.length }} CC address(es)</span>.
          </div>
        </b-alert>
      </div>

      <pre v-if="testOutput" class="test-output mt-1">{{ testOutput }}</pre>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BFormCheckbox, BFormInput, BTable,
  BOverlay, BIcon, BAlert, BSpinner, BBadge, BModal, BFormDatepicker, VBTooltip,
} from 'bootstrap-vue';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import vSelect from 'vue-select';
import reportEmailStoreModule from './reportEmailStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';

const STORE_MODULE = 'reportEmails';

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BFormCheckbox, BFormInput, BTable,
    BOverlay, BIcon, BAlert, BSpinner, BBadge, BModal, BFormDatepicker,
    vSelect, ToastificationContent,
  },

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  setup() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, reportEmailStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE)) store.unregisterModule(STORE_MODULE);
    });
  },

  data() {
    return {
      reports: [],
      summary: { Enabled: false, Emails: [], SendTime: '20:00', Timezone: 'Europe/London' },
      users: [],
      timezones: [],
      logs: [],
      loading: false,
      saving: false,

      testModal: false,
      testReport: null,
      testDate: null,
      testDryRun: true,
      testRunning: false,
      testOutput: '',

      // what the server has, to tell when the screen differs from it
      savedSnapshot: '',
    };
  },

  computed: {
    dirty() {
      return !this.loading && this.savedSnapshot !== '' && this.savedSnapshot !== JSON.stringify(this.payload());
    },

    // lets the summary reuse the per-report test modal
    summaryAsReport() {
      return {
        ReportType: 'summary',
        Label: 'Daily Summary',
        selectedUsers: this.summary.Emails,
        Cc: [],
      };
    },

    logColumns() {
      return [
        { key: 'Label', label: 'Report' },
        { key: 'Email', label: 'Recipient' },
        { key: 'Cc', label: 'CC' },
        { key: 'TradeDate', label: 'Trade Date' },
        { key: 'RowCount', label: 'Rows' },
        { key: 'Status', label: 'Status' },
        { key: 'SentAt', label: 'At' },
        { key: 'Message', label: 'Detail' },
      ];
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      this.loading = true;
      store
        .dispatch('reportEmails/getReportEmailSettings')
        .then((res) => {
          const data = res.data || {};
          this.users = data.users || [];
          this.timezones = data.timezones || [];
          this.logs = data.logs || [];

          // hydrate each report's recipient ids into the user objects v-select needs
          this.reports = (data.reports || []).map((report) => ({
            ...report,
            Cc: report.Cc || [],
            selectedUsers: this.users.filter((u) => (report.userIds || []).includes(u.id)),
          }));

          if (data.summary) {
            this.summary = { ...data.summary, Emails: data.summary.Emails || [] };
          }

          this.savedSnapshot = JSON.stringify(this.payload());
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.showError(`Error loading report email settings: ${this.errorText(e)}`);
        });
    },

    payload() {
      return {
        reports: this.reports.map((report) => ({
          ReportType: report.ReportType,
          Enabled: report.Enabled,
          SendTime: report.SendTime,
          Timezone: report.Timezone,
          Cc: report.Cc,
          userIds: report.selectedUsers.map((u) => u.id),
        })),
        summary: this.summary,
      };
    },

    /** Resolves true once saved, false if saving failed. */
    save() {
      this.saving = true;

      return store
        .dispatch('reportEmails/saveReportEmailSettings', this.payload())
        .then(() => {
          this.saving = false;
          this.showSuccess('Report email settings saved.');
          this.load();
          return true;
        })
        .catch((e) => {
          this.saving = false;
          this.showError(`Error saving: ${this.errorText(e)}`);
          return false;
        });
    },

    openTest(report) {
      this.testReport = report;
      this.testDryRun = true;
      this.testOutput = '';
      const now = new Date();
      this.testDate = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
      ].join('-');
      this.testModal = true;
    },

    runTest() {
      if (!this.testReport || !this.testDate) return;
      this.testRunning = true;
      this.testOutput = '';

      // the run reads the saved settings, so save what is on screen first
      (this.dirty ? this.save() : Promise.resolve(true))
        .then((saved) => {
          if (!saved) throw new Error('Settings could not be saved, so the test was not run.');

          return store.dispatch('reportEmails/runReportEmailNow', {
            reportType: this.testReport.ReportType,
            tradeDate: this.testDate,
            dryRun: this.testDryRun,
          });
        })
        .then((res) => {
          this.testRunning = false;
          this.testOutput = (res.data && res.data.output) || '(no output)';
          if (!this.testDryRun) {
            this.showSuccess('Test run sent.');
            this.load();
          }
        })
        .catch((e) => {
          this.testRunning = false;
          this.showError(`Test run failed: ${this.errorText(e)}`);
        });
    },

    cleanEmail(text) {
      return String(text || '').trim().toLowerCase();
    },

    // drop anything typed that is not an email address, and duplicates
    validEmails(list) {
      const valid = (list || [])
        .map((e) => this.cleanEmail(e))
        .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

      if (valid.length < (list || []).length) {
        const rejected = (list || []).filter((e) => !valid.includes(this.cleanEmail(e)));
        if (rejected.length) this.showError(`Not a valid email address: ${rejected.join(', ')}`);
      }

      return [...new Set(valid)];
    },

    statusVariant(status) {
      if (status === 'sent') return 'light-success';
      if (status === 'failed') return 'light-danger';
      return 'light-secondary';
    },

    errorText(e) {
      return (e.response && e.response.data && e.response.data.message) || e.message;
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text: msg, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },

    showSuccess(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Success', text: msg, icon: 'CheckIcon', variant: 'success' },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/src/assets/scss/compact-design.scss";

.report-block {
  border: 1px solid rgba(34, 41, 47, 0.125);
  box-shadow: none;
}

.summary-block {
  border-style: dashed;
}

// The compact design pins every v-select to one 2.14rem line and stacks the
// selected options on top of each other (fine for single selects). These
// lists need every address visible as a small chip, wrapping onto as many
// lines as it takes, each with its own remove button.
.v-select.email-multi {
  height: auto !important;
  max-height: none !important;
}

.v-select.email-multi::v-deep {
  .vs__dropdown-toggle {
    height: auto;
    max-height: none;
    min-height: 2.14rem;
    padding: 2px 0 3px;
  }

  .vs__selected-options {
    overflow: visible;
    white-space: normal;
    flex-wrap: wrap;
    align-items: center;
    padding: 0 4px;
  }

  .vs__selected {
    position: static !important;
    z-index: auto !important;
    margin: 2px 4px 0 0;
    padding: 1px 4px 1px 8px;
    font-size: 0.72rem;
    line-height: 1.4;
    border: none;
    border-radius: 3px;
    background-color: rgba(11, 70, 102, 0.1);
    color: #0b4666;
  }

  .vs__deselect {
    margin-left: 4px;
    fill: #0b4666;
    svg {
      transform: scale(0.8);
    }
  }

  .vs__search,
  .vs__search:focus {
    flex: 1 1 140px;
    margin: 2px 0 0;
    padding: 0 4px;
    font-size: 0.75rem;
    background: transparent;
  }
}

// disabled: keep the chips readable, just greyed
.v-select.email-multi.vs--disabled::v-deep {
  .vs__selected {
    background-color: rgba(34, 41, 47, 0.08);
    color: #6e6b7b;
  }

  .vs__search {
    background: transparent;
  }
}

.report-email-log {
  font-size: 0.74rem;
}

.test-output {
  background: rgba(34, 41, 47, 0.05);
  border-radius: 0.3rem;
  padding: 0.6rem;
  font-size: 0.72rem;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
}

.card::v-deep .card-body {
  padding: 0.8rem 0.7rem !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
